import React, { useEffect, useState } from 'react';
import {
  StyleSheet, Platform, TouchableOpacity, ScrollView, Alert, View, ActivityIndicator,
} from 'react-native';
import Moment from 'moment';
import * as Linking from 'expo-linking';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import { FontAwesome } from '@expo/vector-icons';
import * as Application from 'expo-application';
import { useDispatch, useSelector } from 'react-redux';
import { Auth } from '@aws-amplify/auth';
import { Text } from '../components/Themed';
import { RootTabScreenProps } from '../types';
import Colors from '../constants/Colors';
import EllipsisIcon from '../components/EllipsisIcon';
import LogoutIcon from '../components/LogoutIcon';
import HomeScreenTopSection from '../components/Home/HomeScreenTopSection';
import BackgroundImage from '../components/BackgroundImage';
import ScreenEmptyState from '../components/ScreenEmptyState';
import HighlightsSection from '../components/Home/HighlightsSection';
import WelcomeModal from '../components/Profile/WelcomeModal';
import signOut from '../components/Signout';
import { updateBadgeCount } from '../redux/selectedBuildingSlice';
import getPushNotificationsToken from '../components/NotificationHelper';
import { logEvent } from '../components/LogEvent';
import UpdateAppModal from '../components/UpdateAppModal';
import getSupportedVersions from '../requests/SignIn/GetSupportedVersions';
import PressableStyled from '../components/PressableStyled';
import refreshToken from '../components/RefreshToken';
import { updateSelectedTicket } from '../redux/selectedTicketSlice';
import HappyHomeMobile from '../components/Home/HappyHomeMobile';
import { screenMaxWidth } from '../components/TopSection';
import GetHomeScreenMetrics from '../requests/Home/GetHomeScreenMetrics';
import getTicket from '../requests/Tickets/GetTicket';
import Style from '../constants/Style';
import { AppScreen } from '../navigation/LinkingConfiguration';
import AlertModal from '../components/AlertModal';
import getTicketStats from '../requests/Tickets/GetTicketStats';
import NoTicketsImage from './Tickets/components/NoTicketsImage';
import NoBuildingsIconMobile from '../components/EmptyState/NoBuildingsIconMobile';
import TermsModal from '../components/Profile/TermsModal';
import { Building } from './LocationManagement/components/AddBuildingModal';
import GetUnreadNotificationsCount from '../requests/Notifications/GetUnreadNotificationsCount';

export default function HomeScreen({ navigation, route }: RootTabScreenProps<'Home'>) {
  const dispatch = useDispatch();
  const { firstName, lastName, acceptedTerms } = useSelector((state:
    { user: { firstName, lastName, acceptedTerms } }) => state.user);
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const buildings: object = useSelector((state:
    { locations: { buildings: object } }) => state.locations.buildings);
  const selectedBuilding: Building | null = (buildings && buildingId)
    ? buildings[buildingId] : null;
  const isAdmin: boolean = useSelector((state:
    { user: { isAdmin: boolean } }) => state.user.isAdmin);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [client, setClient] = useState('');
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [isDev, setIsDev] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  // eslint-disable-next-line no-unused-vars
  const [ticketCounts, setTicketCounts] = useState({
    low: 0,
    normal: 0,
    high: 0,
    critical: 0,
  });

  const [logoutButtonVisible, setLogoutButtonVisible] = useState(false);
  const [welcomeVisible, setWelcomeVisible] = useState<boolean>(false);
  const [termsVisible, setTermsVisible] = useState<boolean>(false);

  const [apiDomain, setApiDomain] = useState('api.hereworks.tech');

  const notEmpty: boolean | undefined | null = useSelector((state:
    { emptyState: { notEmpty: boolean } }) => state.emptyState.notEmpty);

  const isMobile = route.params?.isMobile ?? false;

  const [electricTarget, setElectricTarget] = useState(4000);

  // eslint-disable-next-line no-unused-vars
  const [waterTarget, setWaterTarget] = useState(4000);

  const [metricQuery, setMetricQuery] = useState(null);

  const url = Linking.useURL();

  const showAlert = (message: string) => {
    setAlertMessage(message);
    setAlertVisible(true);
  };

  // TODO: Remove with Expo SDK 54 upgrade
  useEffect(() => {
    const tokenListener = Notifications.addPushTokenListener(() => {
      // console.log(token);
    });

    return () => {
      if (tokenListener) {
        tokenListener.remove();
      }
    };
  }, []);

  const handleTicketError = (message?: string) => {
    showAlert(message ?? 'Unable to load ticket details');
  };

  useEffect(() => {
    if (!url?.length) return;
    const ticketId: string = Linking.parse(url).queryParams?.reportId as string;
    if (ticketId) {
      setLoading(true);
      getTicket(ticketId).then((getTicketResponse) => {
        if (!getTicketResponse || getTicketResponse.isError) {
          handleTicketError(getTicketResponse?.errorText);
        } else dispatch(updateSelectedTicket({ ...getTicketResponse, pinned: true }));
        setLoading(false);
        navigation.navigate(AppScreen.TICKETS);
      }).catch((e) => {
        handleTicketError(e?.message);
        setLoading(false);
      });
    }
  }, [url]);

  useEffect(() => {
    const termsModalToBeShown: boolean = isAdmin && !acceptedTerms;
    if (!firstName || !lastName) {
      AsyncStorage.getItem('happyEnabled').then((val) => {
        if (val === 'enabled') {
          setWelcomeVisible(true);
          // eslint-disable-next-line no-undef
        } else if (termsModalToBeShown) setTermsVisible(true);
      });
    }
    if (termsModalToBeShown) {
      setTermsVisible(true);
    }

    AsyncStorage.getItem('clientName').then(setClient);
    AsyncStorage.getItem('environment').then((env) => setIsDev(env === 'dev'));
    AsyncStorage.getItem('apiDomain').then((currentApi) => {
      setApiDomain(currentApi ?? 'api.hereworks.tech');
    });
    getPushNotificationsToken();
    GetHomeScreenMetrics({
      start: Moment().startOf('day').toISOString(),
      end: Moment().endOf('day').toISOString(),
    }).then((res) => {
      setMetricQuery(res);
      setElectricTarget(res.electricity);
    });
    if (Platform.OS !== 'web') {
      AsyncStorage.getItem('happyEnabled').then((val) => {
        if (val === 'enabled') {
          getSupportedVersions().then((res) => {
            if (res?.details) {
              if (Platform.OS === 'ios') {
                if (Application.nativeBuildVersion < res.details?.ios?.latestVersion) {
                  setUpdateModalVisible(true);
                }
              } else if (Platform.OS === 'android') {
                if (Application.nativeBuildVersion < res.details?.android?.latestVersion) {
                  setUpdateModalVisible(true);
                }
              }
            }
          });
        }
      });
    }
  }, []);

  const refreshNotificationCount: () => void = () => {
    GetUnreadNotificationsCount().then((res) => {
      if (!res) return;
      const count = res.count ?? 0;
      dispatch(updateBadgeCount(count));
      Notifications.requestPermissionsAsync({
        ios: {
          allowAlert: true,
          allowBadge: true,
          allowSound: true,
        },
      }).then((status: Notifications.PermissionResponse) => {
        if (status.granted) {
          Notifications.setBadgeCountAsync(count);
        }
      });
    });
  };

  useEffect(() => {
    if (buildingId) {
      getTicketStats({ buildingId }).then((response) => {
        setTicketCounts({
          low: response?.priorityCounts?.P4 ?? 0,
          normal: response?.priorityCounts?.P3 ?? 0,
          high: response?.priorityCounts?.P2 ?? 0,
          critical: response?.priorityCounts?.P1 ?? 0,
        });
      });
    }
    refreshNotificationCount();
    const subscription = Notifications
      .addNotificationReceivedListener(refreshNotificationCount) as any;
    return () => subscription.remove();
  }, [buildingId]);

  if (!isMobile) {
    return !notEmpty ? <View style={styles.containerWeb}>
      <ScreenEmptyState
        image={buildingId ? <NoTicketsImage /> : <NoBuildingsIconMobile />}
        title={buildingId ? null : 'Loading building...'}
        subtitle={buildingId ? undefined : null}
        large
      />
      <TermsModal
        isVisible={termsVisible}
        dismiss={() => setTermsVisible(false)}
      />
    </View> : <ScrollView style={styles.containerWeb}>
      <BackgroundImage />
      <HomeScreenTopSection setSelectedIndex={setSelectedIndex} />
      <View style={{ maxWidth: screenMaxWidth }}>
        <HighlightsSection
          selectedIndex={selectedIndex}
          ticketCounts={ticketCounts}
          electricValue={Math.round(metricQuery?.processed_energy_accumulator?.value ?? 0)}
          electricTarget={electricTarget}
          waterValue={metricQuery?.processed_water_volume_accumulator?.value}
          waterTarget={waterTarget}
          occupancy={metricQuery?.zone_occupancy_count?.value}
          bookings={metricQuery?.zone_hours_booked?.value}
        />
        <WelcomeModal
          isVisible={welcomeVisible}
          onPress={setWelcomeVisible}
        />
        <TermsModal
          isVisible={termsVisible}
          dismiss={() => setTermsVisible(false)}
        />
      </View>
    </ScrollView>;
  }

  return (
    !notEmpty ? <View style={{ flex: 1 }}>
      <ScreenEmptyState
        image={selectedBuilding ? <NoTicketsImage /> : <NoBuildingsIconMobile />}
        title={buildingId ? null : 'Loading building...'}
        subtitle={buildingId ? undefined : null}
        large
      />
      <TermsModal
        isVisible={termsVisible}
        dismiss={() => setTermsVisible(false)}
      />
    </View> : <View style={{ flex: 1 }}>
      {isDev && <TouchableOpacity
        style={{
          position: 'absolute',
          right: 24,
          top: 120,
          zIndex: 5,
          alignItems: 'flex-end',
        }}
        hitSlop={{
          top: 20,
          bottom: 20,
          left: 50,
          right: 50,
        }}
        onPress={() => {
          logEvent('Home', 'ellipsis');
          setLogoutButtonVisible(!logoutButtonVisible);
        }}
      >
        <EllipsisIcon />
        {logoutButtonVisible && (
          <View style={{
            marginTop: 10,
            backgroundColor: Colors.light.hereworksBlue100,
            borderRadius: 6,
            overflow: 'hidden',
          }}>
            <PressableStyled
              style={{
                backgroundColor: '#fff',
                width: 120,
                height: 36,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onPress={() => {
                if (Platform.OS === 'web') {
                  logEvent('Home', 'sign_out_web');
                  // eslint-disable-next-line no-alert, no-undef
                  signOut();
                } else {
                  Alert.alert(
                    'Sign Out',
                    'Are you sure you want to sign out?',
                    [
                      {
                        text: 'Cancel',
                        style: 'cancel',
                        onPress: () => {
                          logEvent('Home', 'sign_out_cancel');
                          setLogoutButtonVisible(false);
                        },
                      },
                      {
                        text: 'OK',
                        onPress: () => {
                          logEvent('Home', 'sign_out_ok');
                          signOut();
                        },
                      },
                    ],
                    {
                      cancelable: true,
                    },
                  );
                }
              }}
            >
              <LogoutIcon />
              <Text style={styles.menuText}>
                Logout
              </Text>
            </PressableStyled>
            <PressableStyled
              style={{
                backgroundColor: '#fff',
                width: 120,
                height: 36,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 1,
              }}
              onPress={() => {
                logEvent('Home', 'app_info');
                if (Platform.OS === 'web') {
                  // eslint-disable-next-line no-alert, no-undef
                  alert(`Client: ${client}\nAPI: ${apiDomain}`);
                } else {
                  Alert.alert(
                    'App Information',
                    `App Version: ${Application.nativeApplicationVersion} (${Application.nativeBuildVersion})\nClient: ${client}\nAPI: ${apiDomain}`,
                  );
                }
                setLogoutButtonVisible(false);
              }}
            >
              <FontAwesome size={20} name='info-circle' />
              <Text style={styles.menuText}>
                App Info
              </Text>
            </PressableStyled>
            {isDev ? <PressableStyled
              style={{
                backgroundColor: '#fff',
                width: 120,
                height: 36,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 1,
              }}
              onPress={async () => {
                const session = await Auth.currentSession();
                const token = session.getIdToken();
                const expiry = token.getExpiration();
                Alert.alert(Moment.unix(expiry).toString());
              }}>
              <Text style={styles.menuText}>Token Expiry</Text>
            </PressableStyled> : null}
            {isDev ? <PressableStyled
              style={{
                backgroundColor: '#fff',
                width: 120,
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 1,
              }}
              onPress={() => {
                refreshToken(true).then(() => Alert.alert('Token Refreshed!'));
              }}>
              <Text style={styles.menuText}>Force Token Refresh</Text>
            </PressableStyled> : null}
          </View>
        )}
      </TouchableOpacity>}
      <WelcomeModal
        isVisible={welcomeVisible}
        onPress={setWelcomeVisible}
      />
      <TermsModal
        isVisible={termsVisible}
        dismiss={() => setTermsVisible(false)}
      />
      <HappyHomeMobile
        name={firstName}
        isDev={isDev}
      />
      <UpdateAppModal
        isVisible={updateModalVisible}
        hide={() => setUpdateModalVisible(false)}
      />
      <ActivityIndicator
        size='large'
        style={Style.activityIndicator}
        animating={loading}
      />
      <AlertModal
        error
        title={'Error'}
        message={alertMessage}
        modalVisible={alertVisible}
        setModalVisible={setAlertVisible}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  containerWeb: {
    flex: 1,
    backgroundColor: Colors.light.hereworksBackground,
    paddingBottom: 60,
  },
  container: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: Colors.light.hereworksBackground,
    flex: 1,
  },
  scrollView: {
    position: 'absolute',
    top: 300,
    alignSelf: 'center',
    height: 80,
    width: '100%',
    paddingLeft: 16,
  },
  menuText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksPrimaryGrey,
    marginLeft: 8,
    textAlign: 'center',
  },
});
